import { Component } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { RouterLink } from '@angular/router'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faFireFlameCurved, faSearch, faUser } from '@fortawesome/free-solid-svg-icons'
import { Tooltip } from '@grapecity/wijmo'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { CellEditEndingEventArgs, CellRangeEventArgs, CellType, FlexGrid, GridPanel } from '@grapecity/wijmo.grid'
import { NgbNav, NgbNavContent, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase, NgbNavOutlet } from '@ng-bootstrap/ng-bootstrap'
import { QuillViewComponent } from 'ngx-quill'
import { ToDoComponent } from 'src/app/meetings/to-dos/to-do.component'
import { ActionToolbarButtonsComponent } from 'src/app/shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'

import { BaseComponent, Grid, ListItem, ModalBackDrop, ModalSize, ToDo } from '../../shared'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'

@Component({
    selector: 'app-user-to-dos',
    templateUrl: './user-to-dos.component.html',
    standalone: true,
    imports: [
        FormsModule,
        ActionToolbarButtonsComponent,
        ActionToolbarCount,
        ActionToolbarSearchComponent,
        WjGridModule,
        WjGridFilterModule,
        WjInputModule,
        QuillViewComponent,
        FaIconComponent,
        RouterLink,
        NgbNav,
        NgbNavContent,
        NgbNavItem,
        NgbNavItemRole,
        NgbNavLink,
        NgbNavLinkBase,
        NgbNavOutlet,
    ],
})
export class UserToDosComponent extends BaseComponent {
    faFire = faFireFlameCurved
    faSearch = faSearch
    faUser = faUser
    private creatorTooltip = new Tooltip()

    allActiveUserList: ListItem<string>[] = []

    activeTab = 0

    grid: Grid<Partial<ToDo>>
    assignedGrid: Grid<Partial<ToDo>>

    constructor() {
        super()
        this.grid = new Grid<Partial<ToDo>>(this.services)
            .setupModalEditing(
                this.services.data.toDos,
                'To-Do',
                ToDoComponent,
                () => new ToDo(),
                this,
                ModalSize.medium,
                ModalBackDrop.darkAndActive,
            )
            .filterBy(item => [item.name, item.source])
            .doInitialSelection(false)
            .isReadOnly(false)

        this.assignedGrid = new Grid<Partial<ToDo>>(this.services)
            .setupModalEditing(
                this.services.data.toDos,
                'To-Do',
                ToDoComponent,
                () => new ToDo(),
                this,
                ModalSize.medium,
                ModalBackDrop.darkAndActive,
            )
            .filterBy(item => [item.name, item.source, item.assignedToDisplayName])
            .doInitialSelection(false)
            .isReadOnly(false)

        // Refresh both grids when items are added or edited
        this.grid.itemAdded.on(() => {
            this.refresh()
            this.refreshAssigned()
        })
        this.grid.itemEdited.on(() => {
            this.refresh()
            this.refreshAssigned()
        })
        this.assignedGrid.itemAdded.on(() => {
            this.refresh()
            this.refreshAssigned()
        })
        this.assignedGrid.itemEdited.on(() => {
            this.refresh()
            this.refreshAssigned()
        })

        this.loadActiveUsers()
        this.refresh()
        this.refreshAssigned()
    }

    loadActiveUsers() {
        this.cancelableRequest(
            'ActiveUsers',
            true,
            this.services.data.load(true, true, this.services.data.users.queryAllByAction('GetForLookup')).subscribe({
                next: result => {
                    const allUsers = result[0]
                    const allActiveUsers = allUsers.filter((user: any) => user.active)
                    this.allActiveUserList = allActiveUsers.map((user: any) => new ListItem<string>(user.id, user.displayName))
                },
            }),
        )
    }

    refresh() {
        this.cancelableRequest(
            'ToDosRefresh',
            true,
            this.services.data
                .load(
                    true,
                    true,
                    this.services.data.toDos.queryAllByAction('GetByUserId'),
                    this.services.data.get('api/JobOwnership/GetMyItems'), // Don't use 'queryByAction' or it will overwrite our ownership repo
                    this.services.data.get('api/PendingChangeOrder/GetMyApprovals'),
                    this.services.data.get('api/PendingChangeOrder/GetMyPmReadyForApprovalTodos'),
                    this.services.data.get('api/PunchList/GetMyOpenTodos'),
                    this.services.data.get('api/JobHazardAnalysis/GetMyJhaSuperintendentTodos'),
                )
                .subscribe({
                    next: result => {
                        let toDos = result[0] as any[]
                        const jobOwnerships = result[1] as any[]
                        const pcoApprovals = result[2] as any[]
                        const pcoPmReadyForApproval = result[3] as any[]
                        const punchListTodos = result[4] as any[]
                        const jhaSuperTodos = result[5] as any[]

                        toDos.forEach(x => {
                            x.type = 'toDo'
                            x.displayName = x.name
                            if (x.meetingName) {
                                x.source = 'Meeting: ' + x.meetingName
                            } else {
                                x.source = 'Standalone To-Do'
                            }
                        })

                        jobOwnerships.forEach(x => {
                            x.type = 'jobOwnership'
                            x.displayName = x.name
                            x.details = x.notes
                            x.source = 'Job Ownership: ' + x.jobFullName
                        })

                        pcoApprovals.forEach(x => {
                            x.type = 'pendingChangeOrder'
                            x.displayName = `Approve PCO: ${x.description} (${x.pcoNumber})`
                            x.source = `PCO: ${x.job} - ${x.approvalStage}`
                            x.isHot = true
                        })

                        pcoPmReadyForApproval.forEach(x => {
                            x.type = 'pcoPmReadyForApproval'
                            x.displayName = `Ready for Approval: ${x.description} (${x.pcoNumber})`
                            x.name = x.displayName
                            x.source = `PCO: ${x.job}`
                            x.isHot = !x.doneDate
                        })

                        punchListTodos.forEach(x => {
                            x.type = 'punchListItem'
                            x.displayName =
                                `Punch List: ${x.description ?? x.scope ?? ''}` +
                                (x.item != null ? ` (#${x.item})` : '')
                            x.name = x.displayName
                            x.source = `Punch List: ${x.job}`
                            x.isHot = (x.priority ?? 99) <= 1
                        })

                        const jhaTodoRows: any[] = []
                        if (jhaSuperTodos?.length) {
                            const j = jhaSuperTodos[0]
                            jhaTodoRows.push({
                                type: 'jhaSuperintendent',
                                id: j.id,
                                openJhaItemId: j.openJhaItemId,
                                reportCount: j.reportCount,
                                job: j.job,
                                displayName:
                                    j.reportCount > 1
                                        ? `JHA: ${j.reportCount} submitted reports have open non-compliant items`
                                        : `JHA: Open non-compliant items need attention (${j.job})`,
                                name:
                                    j.reportCount > 1
                                        ? `JHA: ${j.reportCount} submitted reports have open non-compliant items`
                                        : `JHA: Open non-compliant items need attention (${j.job})`,
                                source: 'Safety: Job Hazard Analysis',
                                isHot: true,
                            })
                        }

                        toDos = [...toDos, ...jobOwnerships, ...pcoApprovals, ...pcoPmReadyForApproval, ...punchListTodos, ...jhaTodoRows]

                        toDos = toDos.sort((a, b) => {
                            // First, sort by doneDate: items with doneDate (completed) go to the end
                            const aCompleted = a.doneDate != null
                            const bCompleted = b.doneDate != null

                            if (aCompleted !== bCompleted) {
                                return aCompleted ? 1 : -1 // incomplete items first
                            }

                            // For items in the same completion state, sort by isHot (hot items first)
                            if (a.isHot !== b.isHot) {
                                return a.isHot ? -1 : 1 // hot items first
                            }

                            // For items with the same hot status, sort by dueDate
                            if (a.dueDate && b.dueDate) {
                                return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
                            }

                            // Handle cases where dueDate might be null/undefined
                            if (!a.dueDate && !b.dueDate) return 0
                            if (!a.dueDate) return 1
                            if (!b.dueDate) return -1

                            return 0
                        })

                        this.grid.items = toDos
                    },
                }),
        )
    }

    refreshAssigned() {
        this.cancelableRequest(
            'AssignedToDosRefresh',
            true,
            this.services.data.load(true, true, this.services.data.get('api/ToDo/GetByCreatedById')).subscribe({
                next: result => {
                    let toDos = result[0] as any[]

                    toDos.forEach(x => {
                        x.type = 'toDo'
                        x.displayName = x.name
                        if (x.meetingName) {
                            x.source = 'Meeting: ' + x.meetingName
                        } else {
                            x.source = 'Standalone To-Do'
                        }
                    })

                    toDos = toDos.sort((a, b) => {
                        // First, sort by doneDate: items with doneDate (completed) go to the end
                        const aCompleted = a.doneDate != null
                        const bCompleted = b.doneDate != null

                        if (aCompleted !== bCompleted) {
                            return aCompleted ? 1 : -1 // incomplete items first
                        }

                        // For items in the same completion state, sort by isHot (hot items first)
                        if (a.isHot !== b.isHot) {
                            return a.isHot ? -1 : 1 // hot items first
                        }

                        // For items with the same hot status, sort by dueDate
                        if (a.dueDate && b.dueDate) {
                            return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
                        }

                        // Handle cases where dueDate might be null/undefined
                        if (!a.dueDate && !b.dueDate) return 0
                        if (!a.dueDate) return 1
                        if (!b.dueDate) return -1

                        return 0
                    })

                    this.assignedGrid.items = toDos
                },
            }),
        )
    }

    gridItemFormatter = (panel: GridPanel, r, c, cell: HTMLDivElement) => {
        if (panel.cellType === CellType.Cell) {
            const col = panel.grid.columns[c] as any
            const row = panel.grid.rows[r]
            const dataItem = row.dataItem as Partial<ToDo>

            // Reset cell class and style due to cell recycling
            cell.classList.remove('yellow-cell')
            cell.style.textDecoration = 'unset'

            if (col.binding === 'doneDate') cell.classList.add('yellow-cell')

            if (dataItem && dataItem.doneDate != null) {
                cell.style.textDecoration = 'line-through'
            }

            if (col.binding == 'isHot') {
                cell.style.color = dataItem.isHot ? 'red' : 'lightgray'
                if (dataItem.isHot) {
                    cell.children[0].children[0].children[0].classList.add('fa-beat-fade')
                } else {
                    cell.children[0].children[0].children[0].classList.remove('fa-beat-fade')
                }
            }
        }
    }

    assignedGridItemFormatter = (panel: GridPanel, r, c, cell: HTMLDivElement) => {
        if (panel.cellType === CellType.Cell) {
            const col = panel.grid.columns[c] as any
            const row = panel.grid.rows[r]
            const dataItem = row.dataItem as Partial<ToDo>

            // Reset cell class and style due to cell recycling
            cell.classList.remove('yellow-cell')
            cell.style.textDecoration = 'unset'

            if (col.binding === 'doneDate') cell.classList.add('yellow-cell')

            if (dataItem && dataItem.doneDate != null) {
                cell.style.textDecoration = 'line-through'
            }

            if (col.binding == 'isHot') {
                cell.style.color = dataItem.isHot ? 'red' : 'lightgray'
                if (dataItem.isHot) {
                    cell.children[0].children[0].children[0].classList.add('fa-beat-fade')
                } else {
                    cell.children[0].children[0].children[0].classList.remove('fa-beat-fade')
                }
            }
        }
    }

    cellBeginningEdit(flexGrid: FlexGrid, args: CellRangeEventArgs) {
        const dataItem = flexGrid.rows[args.row].dataItem
        if (dataItem?.type === 'pendingChangeOrder' || dataItem?.type === 'pcoPmReadyForApproval' || dataItem?.type === 'punchListItem' || dataItem?.type === 'jhaSuperintendent') {
            args.cancel = true
        }
    }

    cellEditEnded(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        if (args.cancel) return
        this.updateRecords([flexGrid.rows[args.row].dataItem])
    }

    assignedCellEditEnded(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        if (args.cancel) return
        this.updateRecords([flexGrid.rows[args.row].dataItem])
    }

    updateRecords(items: any[]) {
        if (items[0].type === 'toDo') {
            this.services.data.toDos.edit(items[0]).subscribe({
                next: () => {
                    this.grid.refresh()
                    this.assignedGrid.refresh()
                },
                error: () => {
                    this.services.notification.error('The to-do changes were not successfully saved')
                },
            })
        } else if (items[0].type === 'jobOwnership') {
            this.services.data.jobOwnerships.edit(items[0]).subscribe({
                next: () => {
                    this.grid.refresh()
                },
                error: () => {
                    this.services.notification.error('The job ownership changes were not successfully saved')
                },
            })
        } else {
            this.services.notification.error('The item type is not supported')
        }
    }

    showCreatorTooltip(event: MouseEvent, item: Partial<ToDo>) {
        const host = event.currentTarget as HTMLElement
        if (!host) return
        this.creatorTooltip.show(host, this.creatorTooltipText(item))
    }

    hideCreatorTooltip() {
        this.creatorTooltip.hide()
    }

    private creatorTooltipText(item: Partial<ToDo>): string {
        const name = item.createdByDisplayName
        const when = item.createdOn
            ? new Date(item.createdOn).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
            : null
        if (name && when) return `Assigned by ${name} on ${when}`
        if (name) return `Assigned by ${name}`
        if (when) return `Assigned on ${when}`
        return 'Assigned by unknown'
    }

    toggleHot(cell) {
        if (cell.item.type === 'pendingChangeOrder' || cell.item.type === 'pcoPmReadyForApproval' || cell.item.type === 'punchListItem' || cell.item.type === 'jhaSuperintendent') return

        if (cell.item.type === 'toDo') {
            cell.item.isHot = !cell.item.isHot
            this.grid.refresh()
            this.updateRecords([cell.item])
        } else if (cell.item.type === 'jobOwnership') {
            cell.item.isHot = !cell.item.isHot
            this.grid.refresh()
            this.updateRecords([cell.item])
        } else {
            this.services.notification.error('The item type is not supported')
        }
    }

    toggleHotAssigned(cell) {
        if (cell.item.type === 'toDo') {
            cell.item.isHot = !cell.item.isHot
            this.assignedGrid.refresh()
            this.updateRecords([cell.item])
        } else {
            this.services.notification.error('The item type is not supported')
        }
    }

    get completedInPast7Days(): number {
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        const sevenDaysAgo = new Date(today)
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)

        return this.grid.items.filter(item => {
            if (!item.doneDate) return false
            const doneDate = new Date(item.doneDate)
            doneDate.setHours(0, 0, 0, 0)
            return doneDate >= sevenDaysAgo && doneDate <= today
        }).length
    }

    get completedInPast7DaysAssigned(): number {
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        const sevenDaysAgo = new Date(today)
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)

        return this.assignedGrid.items.filter(item => {
            if (!item.doneDate) return false
            const doneDate = new Date(item.doneDate)
            doneDate.setHours(0, 0, 0, 0)
            return doneDate >= sevenDaysAgo && doneDate <= today
        }).length
    }

    tabChanged() {
        // Refresh data when switching tabs if needed
    }
}
